import { useEffect, useState } from "react";

// ─── useUiDensity ──────────────────────────────────────────────────────
//
// Persisted user preference for how wide the dispatch (and shell) page
// container is allowed to grow. Backed by localStorage so the choice
// survives reloads, and kept in sync across independent consumers (e.g.
// the app shell and the density toggle, which never share a React tree)
// via a custom in-tab event plus the native cross-tab `storage` event.

export type UiDensity = "standard" | "wide" | "full";

export const UI_DENSITY_STORAGE_KEY = "ui.dispatch-density";

const DEFAULT_DENSITY: UiDensity = "standard";

// Fired in-tab whenever the density changes so other hook instances in the
// same document re-read the value (the native `storage` event only fires in
// *other* tabs, not the one that wrote it).
const DENSITY_EVENT = "ui-density-change";

/** Map density -> Tailwind max-width class for the page container. */
export const DENSITY_WIDTH_CLASS: Record<UiDensity, string> = {
    standard: "max-w-[1600px]",
    wide: "max-w-[1920px]",
    full: "max-w-none",
};

function isDensity(value: unknown): value is UiDensity {
    return value === "standard" || value === "wide" || value === "full";
}

function readStoredDensity(): UiDensity {
    if (typeof window === "undefined") return DEFAULT_DENSITY;
    const stored = window.localStorage.getItem(UI_DENSITY_STORAGE_KEY);
    return isDensity(stored) ? stored : DEFAULT_DENSITY;
}

export interface UseUiDensityResult {
    density: UiDensity;
    setDensity: (next: UiDensity) => void;
}

export function useUiDensity(): UseUiDensityResult {
    const [density, setDensityState] = useState<UiDensity>(readStoredDensity);

    // Re-sync when another consumer (same tab) or another tab changes it.
    useEffect(() => {
        const sync = () => setDensityState(readStoredDensity());
        window.addEventListener(DENSITY_EVENT, sync);
        window.addEventListener("storage", sync);
        return () => {
            window.removeEventListener(DENSITY_EVENT, sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    const setDensity = (next: UiDensity) => {
        setDensityState(next);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(UI_DENSITY_STORAGE_KEY, next);
            window.dispatchEvent(new CustomEvent(DENSITY_EVENT));
        }
    };

    return { density, setDensity };
}
